// ─── src/services/weddingStore.js ────────────────────────────────────────────
// The single way the app reads/writes the Wedding entity.
//
// Design doctrine: the app thinks in WEDDINGS and SESSIONS, never in browsers.
// This is a pure factory, inject a Supabase client, so the same code runs in
// the app (with the real session client) and in headless staging tests.
//
// Identity: Supabase Anonymous Auth. Ownership is auth.uid() for anonymous AND
// permanent users, so RLS (owner_user_id = auth.uid()) is the only boundary.
// Every mutation appends a wedding_events row, the Evidence log.

import { reasonAboutCandidate } from './weddingReasoning.js';

// State ladder, a role only ever advances (never silently regresses).
export const TEAM_STATES = ['needed', 'suggested', 'exploring', 'shortlisted', 'enquired', 'booked'];

export function createWeddingStore(client) {
  if (!client) return null;

  async function currentUserId() {
    const { data } = await client.auth.getUser();
    return data?.user?.id ?? null;
  }

  // Ensure an auth session exists. A logged-in couple's session is reused as-is;
  // otherwise sign in anonymously. Only ever called at/after the mint threshold,
  // so anonymous users are minted in proportion to real planning intent.
  async function ensureSession() {
    const { data: { session } } = await client.auth.getSession();
    if (session?.user) return session.user;
    const { data, error } = await client.auth.signInAnonymously();
    if (error) throw error;
    return data.user;
  }

  async function logEvent(weddingId, eventType, payload = {}, source = 'aura') {
    if (!weddingId) return;
    await client.from('wedding_events').insert({ wedding_id: weddingId, event_type: eventType, payload, source });
  }

  async function getActiveWedding() {
    const uid = await currentUserId();
    if (!uid) return null;
    const { data, error } = await client
      .from('weddings').select('*')
      .eq('owner_user_id', uid).eq('status', 'active')
      .order('created_at', { ascending: false }).limit(1).maybeSingle();
    if (error) throw error;
    return data ?? null;
  }

  // Get-or-create the single active wedding. Idempotent: the partial unique
  // index guarantees one active wedding per owner, so a mint race resolves to
  // the existing row rather than erroring the caller.
  async function mintWedding(dna = {}) {
    await ensureSession();
    const existing = await getActiveWedding();
    if (existing) return existing;

    const { data, error } = await client.from('weddings').insert({
      destination:     dna.destination     ?? null,
      guest_count:     dna.guestCount       ?? null,
      style:           dna.style            ?? null,
      budget_amount:   dna.budgetAmount     ?? null,
      budget_currency: dna.budgetCurrency   ?? 'GBP',
      confidence:      dna.confidence        ?? null,
    }).select().single();

    if (error) {
      if (String(error.code) === '23505') return await getActiveWedding(); // race → reuse
      throw error;
    }
    await logEvent(data.id, 'wedding_minted', { confidence: dna.confidence ?? null, dna }, 'aura');
    return data;
  }

  const DNA_COLUMN = { destination: 'destination', guestCount: 'guest_count', style: 'style', budgetAmount: 'budget_amount', currentStage: 'current_stage', confidence: 'confidence' };

  async function updateDNA(weddingId, patch = {}) {
    if (!weddingId) return null;
    const cols = {};
    for (const [k, col] of Object.entries(DNA_COLUMN)) if (k in patch) cols[col] = patch[k];
    if (Object.keys(cols).length === 0) return null;

    const { data, error } = await client.from('weddings').update(cols).eq('id', weddingId).select().single();
    if (error) throw error;
    // One granular event per DNA field changed (destination_changed etc. drive act-two recalc).
    for (const k of Object.keys(patch)) if (k in DNA_COLUMN) await logEvent(weddingId, `dna_${k}_set`, { [k]: patch[k] }, 'aura');
    return data;
  }

  async function getTeam(weddingId) {
    if (!weddingId) return [];
    const { data, error } = await client.from('wedding_team').select('*').eq('wedding_id', weddingId);
    if (error) throw error;
    return data ?? [];
  }

  // Advance a role to a state (idempotent per role). Never regresses on the
  // ladder, so a stray "needed" after "shortlisted" is a no-op. Writes an event
  // only on a genuine state change or entity attach.
  async function upsertTeamRole(weddingId, role, opts = {}) {
    if (!weddingId || !role) return null;
    const { state, entityId = null, entityType = null, entityName = null, source = 'aura_suggested' } = opts;
    // confidence column is smallint, scores arrive as floats (e.g. 95.1)
    const confidence = opts.confidence != null ? Math.round(Number(opts.confidence)) : null;

    const { data: existing } = await client.from('wedding_team')
      .select('*').eq('wedding_id', weddingId).eq('role', role).maybeSingle();

    const wanted = state ?? existing?.state ?? 'needed';
    const finalState = existing
      ? (TEAM_STATES.indexOf(wanted) >= TEAM_STATES.indexOf(existing.state) ? wanted : existing.state)
      : wanted;

    const row = {
      wedding_id:  weddingId,
      role,
      state:       finalState,
      entity_id:   entityId   ?? existing?.entity_id   ?? null,
      entity_type: entityType ?? existing?.entity_type ?? null,
      entity_name: entityName ?? existing?.entity_name ?? null,
      source:      source     ?? existing?.source      ?? null,
      confidence:  confidence ?? existing?.confidence  ?? null,
    };

    const { data, error } = await client.from('wedding_team')
      .upsert(row, { onConflict: 'wedding_id,role' }).select().single();
    if (error) throw error;

    const changed = !existing || existing.state !== finalState || existing.entity_id !== row.entity_id;
    if (changed) {
      await logEvent(weddingId, `team_${role}_${finalState}`,
        { role, state: finalState, entity_id: row.entity_id, entity_name: row.entity_name },
        String(source).startsWith('user') ? 'user' : 'aura');
    }
    return data;
  }

  // Record a couple's explicit rejection of a candidate for a role, a negative
  // preference signal. Deposited as grounded evidence (Principle 7), captured from an
  // action the couple already takes (Principle 8). Append-only; survives reload.
  async function logRejection(weddingId, { role, entityId, entityName, reason, reasonCategory } = {}) {
    if (!weddingId || !entityId) return;
    // Reason is OPTIONAL and only added when a couple actually provides one, so existing
    // reasonless dismissals produce a byte-identical payload. When present, the reason rides
    // on the SAME candidate_rejected event Brain 2 already reads, one chance to capture it,
    // never lost. Does not change any Brain 2 logic. See Architecture/10.
    const payload = { role: role ?? null, entity_id: entityId, entity_name: entityName ?? null };
    if (reason != null && reason !== '') payload.reason = reason;
    if (reasonCategory != null && reasonCategory !== '') payload.reason_category = reasonCategory;
    await logEvent(weddingId, 'candidate_rejected', payload, 'user');
  }

  // The entity_ids the couple has dismissed for this wedding, read back from the
  // event log on resume so "Not for us" persists across sessions.
  async function getRejectedEntityIds(weddingId) {
    if (!weddingId) return [];
    const { data, error } = await client
      .from('wedding_events').select('payload')
      .eq('wedding_id', weddingId).eq('event_type', 'candidate_rejected');
    if (error) return [];
    return [...new Set((data ?? []).map((r) => r?.payload?.entity_id).filter(Boolean))];
  }

  const SETTLED = ['shortlisted', 'enquired', 'booked'];

  // Aura suggesting a candidate for a role. Places it as 'exploring' (upsertTeamRole
  // never regresses a settled role) AND logs a durable candidate_suggested snapshot
  // so the gold candidate box survives reload/resume, not just the shortlisted/
  // rejected evidence. Dedups: the snapshot is logged ONLY when the candidate for
  // this role actually changes, and a committed (settled) role is never overwritten.
  async function suggestCandidate(weddingId, role, snap = {}) {
    if (!weddingId || !role || !snap.entityId) return null;
    const { data: existing } = await client.from('wedding_team')
      .select('entity_id,state').eq('wedding_id', weddingId).eq('role', role).maybeSingle();
    if (existing && SETTLED.includes(existing.state)) return { skipped: true };  // committed → leave it
    await upsertTeamRole(weddingId, role, {
      state: 'exploring', entityId: snap.entityId, entityType: snap.entityType ?? null,
      entityName: snap.entityName ?? null, source: 'aura_suggested', confidence: snap.confidence,
    });
    const isNew = !existing || existing.entity_id !== snap.entityId;
    if (isNew) {
      await logEvent(weddingId, 'candidate_suggested', {
        role, entity_id: snap.entityId, entity_type: snap.entityType ?? null, entity_name: snap.entityName ?? null,
        slug: snap.slug ?? null, category: snap.category ?? null,
        country_slug: snap.countrySlug ?? null, region_slug: snap.regionSlug ?? null,
        image: snap.image ?? null, reasons: Array.isArray(snap.reasons) ? snap.reasons : [],
        reasoning_id: snap.reasoningId ?? null,   // one id ties this canvas write back to its full reasoning chain
      }, 'aura');
    }
    return { isNew };
  }

  // Latest suggested candidate per role (the durable snapshots), for rehydrating the
  // gold candidate box on resume. Events are ascending, so the last write per role wins.
  async function getSuggestedCandidates(weddingId) {
    if (!weddingId) return {};
    const { data, error } = await client.from('wedding_events').select('payload, created_at')
      .eq('wedding_id', weddingId).eq('event_type', 'candidate_suggested').order('created_at', { ascending: true });
    if (error || !data) return {};
    const byRole = {};
    for (const e of data) { const p = e.payload || {}; if (p.role && p.entity_id) byRole[p.role] = p; }
    return byRole;
  }

  // Read the evidence behind a team member, the reasons Aura gave, and the journey
  // timeline, from the append-only log. Powers the "why Aura chose them" panel:
  // silent capture, but never secret (the Constitution). One query, filtered in JS.
  async function getTeamMemberEvidence(weddingId, entityId) {
    const empty = { reasons: [], timeline: [] };
    if (!weddingId || !entityId) return empty;
    const { data, error } = await client
      .from('wedding_events').select('event_type, payload, created_at')
      .eq('wedding_id', weddingId).order('created_at', { ascending: true });
    if (error || !data) return empty;
    const mine = data.filter((e) => e?.payload?.entity_id === entityId);
    // latest reasoning wins (a role can be re-reasoned over time)
    const reasoned = [...mine].reverse().find((e) => e.event_type === 'recommendation_reasoned');
    const reasons = Array.isArray(reasoned?.payload?.reasons) ? reasoned.payload.reasons : [];
    // one milestone per state, earliest first (events are already ascending)
    const seen = new Set();
    const timeline = mine
      .filter((e) => typeof e.event_type === 'string' && e.event_type.startsWith('team_'))
      .map((e) => ({ state: e.payload?.state ?? null, at: e.created_at }))
      .filter((t) => t.state && !seen.has(t.state) && seen.add(t.state));
    return { reasons, timeline };
  }

  // Persist the couple's stated timing, the Wedding's heartbeat. Captured only when
  // they actually express a "when" (see parseWeddingTiming). Append-only event too.
  async function setWeddingDate(weddingId, { date = null, label = null } = {}) {
    if (!weddingId) return null;
    const { data, error } = await client.from('weddings')
      .update({ wedding_date: date, date_label: label }).eq('id', weddingId).select().single();
    if (error) return null;
    await logEvent(weddingId, 'wedding_date_set', { date, label }, 'user');
    return data;
  }

  // ── Wedding Scenarios (Guardian) ────────────────────────────────────────────
  // "Committed work" = anything on the current wedding worth protecting. v1: a
  // settled team member (shortlisted / enquired / booked). Extend later to
  // enquiries, notes, supplier messages, comparisons.
  async function hasCommittedWork(weddingId) {
    if (!weddingId) return false;
    const { data, error } = await client.from('wedding_team')
      .select('id').eq('wedding_id', weddingId).in('state', SETTLED).limit(1);
    if (error) return false;
    return (data?.length ?? 0) > 0;
  }

  // Guardian: preserve the current wedding as an alternative and open a fresh
  // active one for the new destination. Each scenario is its own complete wedding
  // (no inheritance), so the new one starts empty and fills with the new place's
  // options. Nothing is deleted; the old direction is one restore away.
  async function forkForNewDestination(currentWedding, dna = {}, newDest = null, conf = null) {
    if (!currentWedding?.id) return null;
    await client.from('weddings')
      .update({ status: 'alternative', label: currentWedding.label ?? currentWedding.destination ?? null })
      .eq('id', currentWedding.id);
    await logEvent(currentWedding.id, 'scenario_preserved', { as: 'alternative', destination: currentWedding.destination ?? null }, 'aura');

    const { data, error } = await client.from('weddings').insert({
      destination: newDest ?? dna.destination ?? null,
      guest_count: dna.guestCount ?? null,
      style:       dna.style ?? null,
      confidence:  conf ?? null,
      forked_from: currentWedding.id,
    }).select().single();
    if (error) {
      // Never strand the couple with no active wedding, undo the preserve.
      await client.from('weddings').update({ status: 'active' }).eq('id', currentWedding.id);
      throw error;
    }
    await logEvent(data.id, 'scenario_forked', { forked_from: currentWedding.id, destination: data.destination }, 'aura');
    return data;
  }

  // New Chat: set the current wedding aside (preserved, not deleted) so the canvas
  // can be cleared for a fresh start. Same mechanism as the Guardian's preserve, but
  // WITHOUT minting a replacement, a new wedding mints only when the couple next
  // describes one. The saved wedding + its conversation return via "Earlier ideas".
  async function preserveActive() {
    const active = await getActiveWedding();
    if (!active?.id) return null;
    await client.from('weddings')
      .update({ status: 'alternative', label: active.label ?? active.destination ?? null })
      .eq('id', active.id);
    await logEvent(active.id, 'conversation_saved', { destination: active.destination ?? null }, 'aura');
    return active.id;
  }

  // Wedding Memory: how many NEW published listings have joined this wedding's
  // destination since the couple was last here. This is the "it worked while you
  // were away" signal, the retention engine. Honest: counts real new inventory in
  // the region/country/city matching the destination string. Returns 0 on any issue.
  async function newListingsSince(destination, sinceIso) {
    if (!destination || !sinceIso) return 0;
    try {
      const pat = `%${String(destination).trim()}%`;
      const { count, error } = await client.from('listings')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'published')
        .gt('created_at', sinceIso)
        .or(`region.ilike.${pat},country.ilike.${pat},city.ilike.${pat}`);
      return error ? 0 : (count ?? 0);
    } catch { return 0; }
  }

  // Every live scenario under this owner, newest first, for the couple-facing
  // "Earlier ideas" list. Never exposes the word "scenario".
  async function listScenarios() {
    const uid = await currentUserId();
    if (!uid) return [];
    const { data, error } = await client.from('weddings')
      .select('id,destination,label,status,guest_count,style,updated_at')
      .eq('owner_user_id', uid).neq('status', 'archived')
      .order('updated_at', { ascending: false });
    if (error) return [];
    return data ?? [];
  }

  // Bring a preserved direction back. Flips the current active to alternative
  // first (there is never more than one active), then the target to active.
  async function restoreScenario(targetId) {
    if (!targetId) return null;
    const current = await getActiveWedding();
    if (current?.id === targetId) return current;
    if (current?.id) {
      await client.from('weddings')
        .update({ status: 'alternative', label: current.label ?? current.destination ?? null })
        .eq('id', current.id);
    }
    const { data, error } = await client.from('weddings')
      .update({ status: 'active' }).eq('id', targetId).select().single();
    if (error) {
      if (current?.id) await client.from('weddings').update({ status: 'active' }).eq('id', current.id);
      throw error;
    }
    await logEvent(targetId, 'scenario_restored', { from: current?.id ?? null, destination: data?.destination ?? null }, 'user');
    return data;
  }

  // The full append-only history for a wedding, ascending, the raw material for
  // "Our Journey" (rendered to sentences client-side) and Wedding Pulse.
  async function getWeddingJourney(weddingId) {
    if (!weddingId) return [];
    const { data, error } = await client.from('wedding_events')
      .select('event_type, payload, created_at').eq('wedding_id', weddingId)
      .order('created_at', { ascending: true });
    if (error) return [];
    return data ?? [];
  }

  return { ensureSession, currentUserId, getActiveWedding, mintWedding, updateDNA, getTeam, upsertTeamRole, logEvent, logRejection, getRejectedEntityIds, getTeamMemberEvidence, suggestCandidate, getSuggestedCandidates, hasCommittedWork, forkForNewDestination, preserveActive, newListingsSince, listScenarios, restoreScenario, setWeddingDate, getWeddingJourney };
}

// ─── Intent → Wedding helpers (pure) ──────────────────────────────────────────
// first non-empty value across a set of possible field aliases on the intent obj
function pick(obj, ...keys) {
  if (!obj || typeof obj !== 'object') return null;
  for (const k of keys) { const v = obj[k]; if (v != null && v !== '') return v; }
  return null;
}

// Is this turn about a VENDOR (photographer, planner, florist…) rather than a venue
// or a destination brief? A vendor query's location is a SEARCH scope ("a
// photographer in Hertfordshire"), NOT where the wedding is, a couple marrying in
// Amalfi can hire a UK photographer who travels. So a vendor turn must never move an
// established wedding destination; the VENUE is what anchors where you marry.
const VENDOR_RE = /planner|photograph|florist|cater|music|entertain|celebrant|videograph|stylist|hair|makeup|vendor|creative/i;
function isVendorTurn(intent) {
  const category = String(pick(intent, 'category', 'category_slug') ?? '');
  const type     = String(pick(intent, 'type') ?? '');
  if (type === 'venue' || /venue/i.test(category)) return false;   // venue anchors destination
  return VENDOR_RE.test(category) || VENDOR_RE.test(type);
}

// Wedding confidence 0-100 from the orchestrator intent. Aura mints silently at
// >= 60. Weighted so a single venue search (destination + category ≈ 45) does
// NOT mint, but a real planning brief (destination + guests + a venue/vendor)
// crosses the line, matching the intended feel (Tuscany 25 → +guests 45 → +villa 65).
export function weddingConfidence(intent) {
  if (!intent || typeof intent !== 'object') return 0;
  const destination = pick(intent, 'location', 'region', 'region_slug', 'country', 'destination');
  const capacity    = pick(intent, 'capacity', 'guest_count', 'guests', 'capacity_max', 'capacity_min');
  const styles      = Array.isArray(intent.styles) ? intent.styles : null;   // real orchestrator: styles[]
  const style       = pick(intent, 'style');
  const category    = String(pick(intent, 'category', 'category_slug') ?? '');
  const type        = String(pick(intent, 'type') ?? '');
  const price       = pick(intent, 'priceSignal', 'price_signal', 'budget', 'budget_signal', 'budget_max_numeric', 'price_from');
  const features    = intent.features;

  let c = 0;
  if (destination) c += 25;
  if (capacity)    c += 20;
  if (/planner|photograph|florist|cater|music|entertain|celebrant|videograph|vendor/i.test(category)) c += 20;
  else if (type === 'venue' || /venue/i.test(category)) c += 20;
  if (price)       c += 15;
  if (style || (styles && styles.length)) c += 10;
  if (Array.isArray(features) && features.length) c += 10;
  return Math.min(100, c);
}

// Extract the DNA fields we persist from an orchestrator intent. Reads the REAL
// orchestrator shape (capacity_min/max, styles[]) and the older synthetic aliases.
// Honesty rule: only claim a guest count the couple ACTUALLY gave, an explicit
// number, or a capacity band the orchestrator flagged with capacity_signal (a
// real size was expressed). A defaulted band with no signal is NOT their choice,
// so we leave it out rather than show a number they never said.
export function dnaFromIntent(intent) {
  const cmin = pick(intent, 'capacity_min');
  const cmax = pick(intent, 'capacity_max');
  let guests = pick(intent, 'guest_count', 'guests', 'capacity');   // explicit number = stated
  if (guests == null && pick(intent, 'capacity_signal') != null) {  // band, but only if a size was expressed
    if (cmin != null && cmax != null) guests = Math.round((Number(cmin) + Number(cmax)) / 2);
    else guests = cmax ?? cmin;
  }
  const styles = Array.isArray(intent?.styles) ? intent.styles : null;
  const style = (styles && styles.length) ? styles[0] : pick(intent, 'style');

  return {
    destination: pick(intent, 'location', 'region', 'country', 'destination'),
    guestCount:  guests != null ? Number(guests) : null,
    style:       style ?? null,
  };
}

// The per-turn assembly: mint at confidence >= 60, sync changed DNA, and place
// the current subject onto the team as Exploring. Single source of truth shared
// by the app store and the staging verification harness. Returns { wedding, team }.
export async function assembleFromTurn(store, currentWedding, intent, recommendations, reasoningId) {
  if (!store || !intent) return { wedding: currentWedding ?? null, team: [], destinationChanged: null, forked: null };

  const conf = weddingConfidence(intent);
  if (!currentWedding && conf < 60) return { wedding: null, team: [], destinationChanged: null, forked: null };  // not enough signal yet

  const dna = dnaFromIntent(intent);

  // Destination is anchored by venues / explicit briefs, never by a vendor search's
  // location. Once a wedding has a destination, a vendor turn's location is ignored
  // for the destination, it's just where we're looking for that vendor.
  // A continuation turn carries no destination the couple stated. "very nice" once
  // rewrote a wedding to the French Riviera: retrieval ran unconstrained, returned a
  // Riviera venue, and the destination followed the RESULTS rather than anything said.
  //
  // SAFEGUARD: no wedding-state write may be derived solely from retrieval results.
  // A destination is written when the couple states it, selects it, or confirms a
  // pending proposal — never because a search happened to land somewhere.
  //
  // Same shape as the vendor lock directly below, which already says destination is
  // anchored by venues and explicit briefs, never by where a search looked. This
  // widens that decision by one case rather than introducing a new idea.
  const isContinuationTurn = intent?.is_continuation === true;
  const destinationLocked = (isVendorTurn(intent) && !!(currentWedding && currentWedding.destination))
    || isContinuationTurn;
  const destSignal = destinationLocked ? null : dna.destination;

  let w = currentWedding ?? await store.mintWedding({ ...dna, destination: destSignal ?? dna.destination, confidence: conf });
  if (!w) return { wedding: null, team: [], destinationChanged: null, forked: null };

  // A real destination pivot (had one, now a different one).
  const destinationChanged = (currentWedding && currentWedding.destination && destSignal && destSignal !== currentWedding.destination)
    ? { from: currentWedding.destination, to: destSignal }
    : null;

  // Guardian: a pivot that would overwrite COMMITTED work forks a new scenario
  // instead of clobbering it. Preserve the current wedding as an alternative and
  // continue on a fresh active one. If nothing is committed yet, edit in place
  // (lazy fork, a scenario always means you diverged from something real).
  let forked = null;
  if (destinationChanged && await store.hasCommittedWork(currentWedding.id)) {
    const forkedWedding = await store.forkForNewDestination(currentWedding, dna, destSignal, conf);
    if (forkedWedding) {
      w = forkedWedding;
      forked = { keptLabel: currentWedding.destination, keptId: currentWedding.id, toDestination: destSignal };
    }
  }

  // Sync only CHANGED DNA (keeps the event log meaningful). After a fork, w is the
  // fresh scenario already carrying the new destination, so destination is a no-op.
  const patch = {};
  if (destSignal && destSignal !== w.destination) patch.destination = destSignal;
  if (dna.guestCount  && dna.guestCount  !== w.guest_count) patch.guestCount  = dna.guestCount;
  if (dna.style       && dna.style       !== w.style)       patch.style       = dna.style;
  let wedding = w;
  if (Object.keys(patch).length) wedding = (await store.updateDNA(w.id, patch)) ?? w;

  // Team assembly: the current subject → Exploring, but ONLY if Aura can justify
  // it for this wedding (golden rule; keeps the team itself honest).
  // `promotion` is the trace record of THIS seam, the client-side promotion gate. It
  // makes "why did Budapest get promoted (or not)?" inspectable: eligible/explainable
  // and the reasons/concerns behind the decision.
  let promotion = { promoted: false, reason: 'no_candidates' };
  const top = recommendations?.items?.[0];
  if (top) {
    const role = roleFromCategory(top.categorySlug ?? top.category);
    const judged = reasonAboutCandidate({ destination: wedding.destination, style: wedding.style, guest_count: wedding.guest_count }, top, { role });
    promotion = {
      promoted: judged.recommend, role, entity: top.name, entity_country: top.country ?? null,
      eligible: judged.eligible, explainable: judged.explainable, confidence: judged.confidence,
      reasons: judged.reasons, concerns: judged.concerns,
    };
    if (judged.recommend) {
      // Place as exploring AND persist the durable candidate snapshot so the gold
      // box survives reload (dedups internally; never overwrites a settled role).
      await store.suggestCandidate(w.id, role, {
        entityId: top.id, entityType: top.type, entityName: top.name,
        slug: top.slug, category: top.categorySlug ?? top.category,
        countrySlug: top.countrySlug, regionSlug: top.regionSlug,
        image: Array.isArray(top.imgs) ? top.imgs[0] : null,
        reasons: judged.reasons, confidence: judged.confidence,
        reasoningId: reasoningId ?? null,
      });
    }
  }

  const team = await store.getTeam(w.id);
  return { wedding, team, destinationChanged, forked, promotion };
}

// Map a listing category slug to a wedding-team role. Free-text by design, so
// the taxonomy scales forever (jeweller, yacht, honeymoon…) with no schema change.
export function roleFromCategory(categorySlug) {
  const c = String(categorySlug ?? '').toLowerCase();
  if (!c || c.includes('venue'))       return 'venue';
  if (c.includes('planner'))           return 'planner';
  if (c.includes('photograph'))        return 'photographer';
  if (c.includes('videograph'))        return 'videographer';
  if (c.includes('florist') || c.includes('flower')) return 'florist';
  if (c.includes('cater'))             return 'catering';
  if (c.includes('music') || c.includes('entertain') || c.includes('dj')) return 'entertainment';
  if (c.includes('celebrant'))         return 'celebrant';
  return 'vendor';
}
